import { lazy, Suspense, Fragment } from 'react';
import { LIVE_LOCALES, LOCALES } from '@/lib/locales';
import CookieConsent from '@/components/locale/CookieConsent';
import LangPathSync from '@/components/locale/LangPathSync';
import CircuitClickEffect from '@/components/CircuitClickEffect';
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { LanguageProvider } from '@/lib/LanguageContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import Home from './pages/Home';
import BrandLoader from '@/components/BrandLoader';

// Route-level code splitting: keep only the landing page (Home) in the initial
// bundle and lazy-load every other route so first-visit transfer size stays small.
const loadAbout = () => import('./pages/About');
const loadContact = () => import('./pages/Contact');
const loadServices = () => import('./pages/Services');
const loadServiceDetail = () => import('./pages/ServiceDetail');
const loadBlog = () => import('./pages/Blog');
const loadBlogPost = () => import('./pages/BlogPost');
const About = lazy(loadAbout);
const Contact = lazy(loadContact);
const Services = lazy(loadServices);
const ServiceDetail = lazy(loadServiceDetail);
const Blog = lazy(loadBlog);
const loadPricing = () => import('./pages/Pricing');
const loadFounder = () => import('./pages/Founder');
const loadPartners = () => import('./pages/Partners');
const Pricing = lazy(loadPricing);
const BlogPost = lazy(loadBlogPost);
const Founder = lazy(loadFounder);
const Partners = lazy(loadPartners);
const Ads = lazy(() => import('./pages/Ads'));

// Preload every menu page in the background right after first paint so navigation is instant.
if (typeof window !== 'undefined') {
  const preload = () => [loadServices, loadPricing, loadFounder, loadAbout, loadContact, loadBlog, loadPartners, loadServiceDetail, loadBlogPost].forEach((l) => l());
  if (document.readyState === 'complete') setTimeout(preload, 300);
  else window.addEventListener('load', () => setTimeout(preload, 300), { once: true });
}
const Login = lazy(() => import('./pages/Login.jsx'));
const Register = lazy(() => import('./pages/Register.jsx'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword.jsx'));
const ResetPassword = lazy(() => import('./pages/ResetPassword.jsx'));
const Portal = lazy(() => import('./pages/Portal.jsx'));
const AcceptInvite = lazy(() => import('./pages/portal/AcceptInvite.jsx'));
const loadLocaleHub = () => import('./pages/locale/LocaleHub');
const loadLocaleService = () => import('./pages/locale/LocaleService');
const LocaleHub = lazy(loadLocaleHub);
const LocaleService = lazy(loadLocaleService);
const LocalePrivacy = lazy(() => import('./pages/locale/LocalePrivacy'));
if (typeof window !== 'undefined') {
  const preloadLocales = () => { loadLocaleHub(); loadLocaleService(); };
  if (document.readyState === 'complete') setTimeout(preloadLocales, 800);
  else window.addEventListener('load', () => setTimeout(preloadLocales, 800), { once: true });
}

const RouteFallback = BrandLoader;

// Block right-click "save image" and dragging on media (does not affect crawlers).
if (typeof window !== 'undefined') {
  const block = (e) => { if (e.target.closest?.('img, video, canvas')) e.preventDefault(); };
  document.addEventListener('contextmenu', block);
  document.addEventListener('dragstart', block);
}

const AuthenticatedApp = () => {
  const { isLoadingAuth } = useAuth();

  // Show loading spinner while checking auth
  if (isLoadingAuth) {
    return <BrandLoader />;
  }

  // Render the main app — every route gated behind our custom auth
  return (
    <>
      <ScrollToTop />
      <LangPathSync />
      <CookieConsent />
      <CircuitClickEffect />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/portal/accept-invite" element={<AcceptInvite />} />
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/custom-ai-solutions-dubai" element={<ServiceDetail slug="custom-ai-solutions-dubai" />} />
        <Route path="/services/website-development-dubai" element={<ServiceDetail slug="website-development-dubai" />} />
        <Route path="/services/landing-page-design-dubai" element={<ServiceDetail slug="landing-page-design-dubai" />} />
        <Route path="/services/saas-development-dubai" element={<ServiceDetail slug="saas-development-dubai" />} />
        <Route path="/services/mobile-app-development-dubai" element={<ServiceDetail slug="mobile-app-development-dubai" />} />
        <Route path="/services/game-development-uae" element={<ServiceDetail slug="game-development-uae" />} />
        <Route path="/services/business-automation-dubai" element={<ServiceDetail slug="business-automation-dubai" />} />
        <Route path="/services/digital-marketing-dubai" element={<ServiceDetail slug="digital-marketing-dubai" />} />
        <Route path="/services/cybersecurity-ai-uae" element={<ServiceDetail slug="cybersecurity-ai-uae" />} />
        <Route path="/us" element={<LocaleHub cc="us" />} />
        <Route path="/ca" element={<LocaleHub cc="ca" />} />
        <Route path="/au" element={<LocaleHub cc="au" />} />
        <Route path="/es" element={<LocaleHub cc="es" />} />
        <Route path="/mx" element={<LocaleHub cc="mx" />} />
        <Route path="/us/services/custom-ai-development" element={<LocaleService cc="us" slug="custom-ai-development" />} />
        <Route path="/us/services/business-process-automation" element={<LocaleService cc="us" slug="business-process-automation" />} />
        <Route path="/us/services/ai-cybersecurity" element={<LocaleService cc="us" slug="ai-cybersecurity" />} />
        <Route path="/us/services/web-and-saas-development" element={<LocaleService cc="us" slug="web-and-saas-development" />} />
        <Route path="/ca/services/custom-ai-development" element={<LocaleService cc="ca" slug="custom-ai-development" />} />
        <Route path="/ca/services/business-process-automation" element={<LocaleService cc="ca" slug="business-process-automation" />} />
        <Route path="/ca/services/ai-cybersecurity" element={<LocaleService cc="ca" slug="ai-cybersecurity" />} />
        <Route path="/ca/services/web-and-saas-development" element={<LocaleService cc="ca" slug="web-and-saas-development" />} />
        <Route path="/au/services/custom-ai-development" element={<LocaleService cc="au" slug="custom-ai-development" />} />
        <Route path="/au/services/business-process-automation" element={<LocaleService cc="au" slug="business-process-automation" />} />
        <Route path="/au/services/ai-cybersecurity" element={<LocaleService cc="au" slug="ai-cybersecurity" />} />
        <Route path="/au/services/web-and-saas-development" element={<LocaleService cc="au" slug="web-and-saas-development" />} />
        <Route path="/es/servicios/desarrollo-de-ia-a-medida" element={<LocaleService cc="es" slug="desarrollo-de-ia-a-medida" />} />
        <Route path="/es/servicios/automatizacion-de-procesos" element={<LocaleService cc="es" slug="automatizacion-de-procesos" />} />
        <Route path="/es/servicios/ciberseguridad-con-ia" element={<LocaleService cc="es" slug="ciberseguridad-con-ia" />} />
        <Route path="/es/servicios/desarrollo-web-y-saas" element={<LocaleService cc="es" slug="desarrollo-web-y-saas" />} />
        <Route path="/mx/servicios/desarrollo-de-ia-a-medida" element={<LocaleService cc="mx" slug="desarrollo-de-ia-a-medida" />} />
        <Route path="/mx/servicios/automatizacion-de-procesos" element={<LocaleService cc="mx" slug="automatizacion-de-procesos" />} />
        <Route path="/mx/servicios/ciberseguridad-con-ia" element={<LocaleService cc="mx" slug="ciberseguridad-con-ia" />} />
        <Route path="/mx/servicios/desarrollo-web-y-saas" element={<LocaleService cc="mx" slug="desarrollo-web-y-saas" />} />
        <Route path="/us/privacy-policy" element={<LocalePrivacy cc="us" />} />
        <Route path="/ca/privacy-policy" element={<LocalePrivacy cc="ca" />} />
        <Route path="/au/privacy-policy" element={<LocalePrivacy cc="au" />} />
        <Route path="/es/politica-de-privacidad" element={<LocalePrivacy cc="es" />} />
        <Route path="/mx/aviso-de-privacidad" element={<LocalePrivacy cc="mx" />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/prices" element={<Pricing />} />
        <Route path="/founder" element={<Founder />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/ads" element={<Ads />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        {LIVE_LOCALES.map((cc) => (
          <Fragment key={cc}>
            <Route path={`/${cc}`} element={<LocaleHub cc={cc} />} />
            <Route path={`/${cc}/${LOCALES[cc].seg}/:slug`} element={<LocaleService cc={cc} />} />
            <Route path={`/${cc}/${LOCALES[cc].privacy}`} element={<LocalePrivacy cc={cc} />} />
          </Fragment>
        ))}
        <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
          <Route path="/portal/*" element={<Portal />} />
        </Route>
        <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <LanguageProvider>
          <Router>
            <AuthenticatedApp />
          </Router>
          <Toaster />
        </LanguageProvider>
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App